import type { GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { HookGeneratorResult } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import { pluralize, singularize, toCamelCase, toPascalCase } from '@cli-utils/text.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { resolveEntityFilters } from '@next-adapter/generators/entity-filters/resolve-entity-filters.js'
import { renderHookRenderer } from '@next-adapter/templates/renderers/generators/hook/generate-hook.js'

export function generateHook(
  schema: Schema,
  hooksDir: string,
  options: GeneratorOptions = {}
): HookGeneratorResult {
  const hookFileName = `use-${schema.name}.ts`
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)

  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)

  const dbFields = flattenFields(schema.fields)
  const hasSlugField = dbFields.some((f) => f.name === 'slug')

  const resolvedFilters = resolveEntityFilters(schema).map((filter) => ({
    ...filter,
    variableName: toCamelCase(filter.key),
    pascalName: toPascalCase(filter.key),
    fromVariableName: `${toCamelCase(filter.key)}From`,
    toVariableName: `${toCamelCase(filter.key)}To`
  }))
  const hasFilters = resolvedFilters.length > 0

  const actionImports: string[] = [`get${Plural}`, `get${Singular}ById`]
  if (hasSlugField) actionImports.push(`get${Singular}BySlug`)
  if (hasFilters) {
    for (const filter of resolvedFilters) {
      if (filter.source === 'distinct') {
        actionImports.push(`getDistinct${Plural}${filter.pascalName}`)
      }
    }
  }
  const typeImports = [`type ${Singular}`, `type ${Plural}Page`, `type ${Plural}Query`]

  const filterParams = hasFilters
    ? resolvedFilters
        .flatMap((filter) =>
          filter.type === 'date-range'
            ? [`${filter.fromVariableName}?: string`, `${filter.toVariableName}?: string`]
            : [`${filter.variableName}?: string`]
        )
        .join(', ')
    : ''
  const paginationParams =
    "page?: number, pageSize?: number, orderBy?: string, orderDirection?: 'asc' | 'desc'"
  const allParams = filterParams
    ? `search?: string, ${filterParams}, ${paginationParams}`
    : `search?: string, ${paginationParams}`

  const queryKeyParts = [
    "search ?? ''",
    ...(hasFilters
      ? resolvedFilters.flatMap((filter) =>
          filter.type === 'date-range'
            ? [`${filter.fromVariableName} ?? ''`, `${filter.toVariableName} ?? ''`]
            : [`${filter.variableName} ?? ''`]
        )
      : []),
    'safePage',
    'safePageSize',
    "orderBy ?? ''",
    "orderDirection ?? ''"
  ].join(', ')

  const paginationBlock = renderHookRenderer({ kind: 'renderHookContentTemplate' })

  const filtersBody = renderHookRenderer({
    kind: 'renderHookFiltersTemplate',
    props: {
      plural: Plural,
      paginationBlock: paginationBlock,
      map: resolvedFilters
        .map((filter) => {
          if (filter.builtIn === 'status') {
            return renderHookRenderer({ kind: 'renderHookContentDetailsTemplate' })
          }

          if (filter.type === 'date-range') {
            return renderHookRenderer({
              kind: 'renderHookContentDetailsBranchTemplate',
              props: {
                filterFromVariableName: filter.fromVariableName,
                filterField: filter.field,
                filterToVariableName: filter.toVariableName
              }
            })
          }

          if (filter.type === 'boolean') {
            return renderHookRenderer({
              kind: 'renderHookContentDetailsBranchBodyTemplate',
              props: {
                filterVariableName: filter.variableName,
                filterField: filter.field
              }
            })
          }

          return `      if (${filter.variableName}) filters.${filter.field} = ${filter.variableName}`
        })
        .join('\n')
    }
  })

  const singularHook = renderHookRenderer({
    kind: 'renderHookGeneratedFunctionTemplate',
    props: {
      singular: Singular,
      singularText: singular
    }
  })

  const slugHook = hasSlugField
    ? renderHookRenderer({
        kind: 'renderHookGeneratedFunctionDetailsTemplate',
        props: {
          singular: Singular,
          singularText: singular
        }
      })
    : ''

  const distinctHooks = hasFilters
    ? resolvedFilters
        .filter((filter) => filter.source === 'distinct')
        .map((filter) =>
          renderHookRenderer({
            kind: 'renderHookGeneratedFunctionDetailsBranchTemplate',
            props: {
              plural: Plural,
              filterPascalName: filter.pascalName,
              pluralText: plural,
              filterKey: filter.key
            }
          })
        )
        .join('')
    : ''

  const content = renderHookRenderer({
    kind: 'renderHookGeneratedFunctionDetailsBranchBodyTemplate',
    props: {
      join: [...actionImports, ...typeImports].join(',\n  '),
      actionImportPath: actionImportPath,
      plural: Plural,
      allParamsContent: allParams ? `${allParams},` : '',
      pluralText: plural,
      queryKeyParts: queryKeyParts,
      filtersBody: filtersBody,
      singularHook: singularHook,
      slugHook: slugHook,
      distinctHooks: distinctHooks
    }
  })

  return {
    files: [createGeneratedFile(`${hooksDir}/${hookFileName}`, content)],
    hookName: `use${Plural}`
  }
}
